/**
 * Блок ② — ДОКАЗАТЕЛЬСТВО под карточкой ответа, и заголовка у него больше нет
 * (UX_SPEC §2, §3.1). «Активно падает» было четвёртым голосом о состоянии поля рядом с
 * карточкой: состояние поля называет карточка и только она. Здесь остаётся то, чем она
 * подкреплена, — датированное наблюдение и уровень, на котором оно снято.
 *
 * Порядок внутри блока: квалификаторы одной строкой («пик пройден · уверенность высокая»),
 * полоска уровня с датированной строкой-подписью, числа NDVI за раскрытием «при числах».
 * Полоса состояния целиком уехала в СВОЙ блок ③ (`Console`): у неё свой знаменатель и своя
 * строка-summary, и жить внутри доказательства она больше не может.
 *
 * Блока без содержимого в DOM нет (§12.1): пока прогона нет или он идёт, компонент
 * возвращает `null`, а объяснение отсутствия принадлежит карточке и печатается один раз.
 */

import { T } from "../strings";
import type { Run } from "../api/types";
import { formatDate, num, signed } from "../lib/format";
import { vegetationFromRun, vegetationGapReason } from "../lib/vegetation";
import { LevelBar } from "./LevelBar";
import { RadarSeries } from "./RadarSeries";

interface Props {
  run: Run | null;
  revealKey: number;
  /** Служебный режим: только он добавляет к строкам их технические скобки. */
  devMode: boolean;
  /**
   * «Числа» ИЛИ служебный режим. Закрывает числа NDVI, ноту влажности и число пика;
   * датированную строку доказательства и полоску уровня — никогда.
   */
  showNumbers: boolean;
}

const Row = ({ label, value }: { label: string; value: string }) => (
  <>
    <dt>{label}</dt>
    <dd>{value}</dd>
  </>
);

export function Vegetation({ run, revealKey, devMode, showNumbers }: Props) {
  if (run === null || run.status === "running") return null;

  const view = vegetationFromRun(run);
  // Ряд радара стоит на продуктовой поверхности только тогда, когда на нём стоит ответ: он
  // тогда ДОКАЗАТЕЛЬСТВО и обязан быть в одном экране с вердиктом. На обычном прогоне слова
  // «радар» на экране нет ни разу — сам ряд лежит в служебной панели.
  const radarAnswered = run.payload?.answered_by_radar === true;

  if (view === null) {
    // Отсылки в служебные данные здесь больше нет: причина названа прямо, одной строкой, а
    // журнал отклонённых дат раскрывается в блоке снимков при включённых числах (§12).
    return (
      <div className="k-reveal" key={revealKey}>
        <p className="k-note k-note--info" data-testid="vegetation-reason">
          {vegetationGapReason(run)}
        </p>
        {radarAnswered && <RadarSeries run={run} dev={devMode} />}
      </div>
    );
  }

  // Квалификатор наблюдения — только положение к пику. Ярлык уверенности с карточки УШЁЛ
  // (confidence-inline-move, §11.1): он живёт в раскрытии «Откуда мы это знаем» ниже и на
  // карточку не возвращается никогда — «уверенность» рядом с ответом читалась бы
  // вероятностью, которой не мерили.
  const qualifiers = [view.positionLabel].filter((part): part is string => part !== null);

  return (
    <div className="k-reveal" key={revealKey}>
      {/* `vegetation-state` переехал СЮДА с удалённого h3: тесты различали блок по нему, а
          заголовка у блока больше нет. */}
      {qualifiers.length > 0 && (
        <p className="k-veg__position" data-testid="vegetation-state">
          {qualifiers.join(" · ")}
        </p>
      )}

      {view.stale && <p className="k-note k-note--warn">{T.vegetation.staleWarning}</p>}

      {/* Датированное наблюдение и уровень, на котором оно снято. Дата берётся с САМОГО
          снимка (`latestClearDate`), а не с даты анализа: между ними бывает три недели. */}
      <LevelBar
        ndviNow={view.ndviNow}
        peakNdvi={view.peakNdvi}
        observationDate={view.latestClearDate}
        peakDate={view.peakDate}
        showNumbers={showNumbers}
      />

      {/* Подтверждение спада по второму каналу — техническая строка: она не меняет ответ,
          а объясняет, чем он подпёрт. */}
      {view.removalSupported && showNumbers && (
        <p className="k-note k-note--info" data-testid="vegetation-removal">
          {T.vegetation.removalSupported}
          {devMode && view.supportFlags.length > 0 ? ` (${view.supportFlags.join("; ")})` : ""}
        </p>
      )}

      {/* Числа NDVI целиком — «при числах»: строка доказательства и полоска уровня говорят
          то же самое словами, а раскрытие отвечает на вопрос «а сколько именно». */}
      {showNumbers && (
        <details className="k-details k-section k-section--tight">
          <summary>{T.vegetation.numbersTitle}</summary>
          {/* Полная фраза сервера (`vegetation_state_ru`) живёт ЗДЕСЬ: она говорит словарём
              пайплайна, а на продуктовой поверхности два словаря на один ответ читались бы
              как спор. */}
          {view.sentence && (
            <p className="k-caption" data-testid="vegetation-sentence">
              {view.sentence}
            </p>
          )}
          {/* «Уверенность измерения» УДАЛЕНА 2026-09-05 (решение владельца,
              (правила текстов — ТЗ.md): внутренняя шкала, продукт её не носит ни на одном
              ярусе — код `confidence` остаётся в payload для яруса dev. */}
          <dl className="k-readout">
            <Row label={T.vegetation.numbers.ndviNow} value={num(view.ndviNow, 3)} />
            <Row label={T.vegetation.numbers.change30} value={signed(view.change30, 3)} />
            <Row
              label={T.vegetation.numbers.peakDate}
              value={
                view.peakDate === null
                  ? T.common.noData
                  : `${formatDate(view.peakDate)} · ${num(view.peakNdvi, 3)}`
              }
            />
            <Row label={T.vegetation.numbers.dropFromPeak} value={num(view.dropFromPeak, 3)} />
          </dl>
        </details>
      )}

      {radarAnswered && <RadarSeries run={run} dev={devMode} />}
    </div>
  );
}
